import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  GraduationCap, Users, Calendar, Settings, BookOpen, 
  ChevronRight, Leaf, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ========== EXAM CALENDAR TAB ==========
function ExamCalendarTab({ country }) {
  const [calendarConfig, setCalendarConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingExam, setEditingExam] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const fetchCalendarConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/education-tuition/admin/exam-calendar/${country}`
      );
      const data = await response.json();
      if (data.success) {
        setCalendarConfig(data.config);
      }
    } catch (error) {
      console.error('Failed to fetch exam calendar:', error);
      toast.error('Failed to fetch exam calendar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarConfig();
  }, [country]);

  const toggleCalendar = async (enabled) => {
    try {
      const response = await fetch(
        `${API_URL}/api/education-tuition/admin/exam-calendar/${country}/toggle?enabled=${enabled}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchCalendarConfig();
      }
    } catch (error) {
      toast.error('Failed to toggle exam calendar');
    }
  };

  const saveExam = async (exam) => {
    try {
      const response = await fetch(
        `${API_URL}/api/education-tuition/admin/exam-calendar/${country}/exam`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(exam),
        }
      );
      const data = await response.json();
      if (data.success) {
        toast.success('Exam saved');
        fetchCalendarConfig();
        setEditingExam(null);
        setShowAddDialog(false);
      }
    } catch (error) {
      toast.error('Failed to save exam');
    }
  };

  const deleteExam = async (examId) => {
    if (!window.confirm('Delete this exam?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/education-tuition/admin/exam-calendar/${country}/${examId}`,
        { method: 'DELETE' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success('Exam deleted');
        fetchCalendarConfig();
      }
    } catch (error) {
      toast.error('Failed to delete exam');
    }
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-48 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-100 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Exam Calendar
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Manage exam dates for {country === 'UK' ? 'United Kingdom' : 'Sri Lanka'}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="calendar-toggle" className="text-sm">Enabled</Label>
              <Switch 
                id="calendar-toggle"
                checked={calendarConfig?.enabled ?? true}
                onCheckedChange={toggleCalendar}
              />
            </div>
            <Button onClick={() => setShowAddDialog(true)}>
              + Add Exam
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Exam</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Exam Period</TableHead>
              <TableHead>Prep Starts</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {calendarConfig?.exams?.map((exam) => (
              <TableRow key={exam.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{exam.icon}</span>
                    <div>
                      <p className="font-medium">{exam.title}</p>
                      <p className="text-xs text-gray-500 line-clamp-1">{exam.description}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{exam.exam_type}</Badge>
                </TableCell>
                <TableCell>
                  {exam.start_month}{exam.end_month !== exam.start_month ? ` - ${exam.end_month}` : ''}
                </TableCell>
                <TableCell>{exam.prep_start_month}</TableCell>
                <TableCell>
                  {exam.is_active ? (
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingExam(exam)}>
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-600"
                      onClick={() => deleteExam(exam.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Add/Edit Dialog */}
        {(showAddDialog || editingExam) && (
          <ExamDialog
            exam={editingExam}
            onSave={saveExam}
            onClose={() => { setShowAddDialog(false); setEditingExam(null); }}
            months={months}
          />
        )}
      </CardContent>
    </Card>
  );
}

// Exam Edit Dialog
function ExamDialog({ exam, onSave, onClose, months }) {
  const [formData, setFormData] = useState(exam || {
    title: '',
    description: '',
    exam_type: 'national',
    icon: '📝',
    image_url: '',
    start_month: 'January',
    end_month: 'January',
    prep_start_month: 'January',
    highlight_text: '',
    subcategory_filter: 'exam-preparation',
    is_active: true,
    display_order: 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">
          {exam ? 'Edit Exam' : 'Add Exam'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Title *</Label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., GCSE Exams"
              />
            </div>
            
            <div className="col-span-2">
              <Label>Description</Label>
              <Input 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div>
              <Label>Icon</Label>
              <Input 
                value={formData.icon}
                onChange={(e) => setFormData({...formData, icon: e.target.value})}
              />
            </div>

            <div>
              <Label>Exam Type</Label>
              <Select 
                value={formData.exam_type}
                onValueChange={(v) => setFormData({...formData, exam_type: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="national">National</SelectItem>
                  <SelectItem value="entrance">Entrance</SelectItem>
                  <SelectItem value="scholarship">Scholarship</SelectItem>
                  <SelectItem value="language">Language</SelectItem>
                  <SelectItem value="university">University</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Exam Start Month</Label>
              <Select 
                value={formData.start_month}
                onValueChange={(v) => setFormData({...formData, start_month: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Exam End Month</Label>
              <Select 
                value={formData.end_month}
                onValueChange={(v) => setFormData({...formData, end_month: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Prep Start Month</Label>
              <Select 
                value={formData.prep_start_month}
                onValueChange={(v) => setFormData({...formData, prep_start_month: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Display Order</Label>
              <Input 
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
              />
            </div>

            <div className="col-span-2">
              <Label>Image URL</Label>
              <Input 
                value={formData.image_url || ''}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
              />
            </div>

            <div className="col-span-2 flex items-center gap-2">
              <Switch 
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({...formData, is_active: v})}
              />
              <Label>Active</Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">Save Exam</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ========== MAIN ADMIN COMPONENT ==========
export default function AdminEducationTuition() {
  const [selectedCountry, setSelectedCountry] = useState('UK');
  const [activeTab, setActiveTab] = useState('subcategories');
  const [stats, setStats] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/education-tuition/admin/stats?country=${selectedCountry}`);
      const statsData = await statsRes.json();
      if (statsData.success) {
        setStats(statsData.stats);
      }

      // Fetch subcategories
      const subcatRes = await fetch(`${API_URL}/api/education-tuition/config/subcategories?country=${selectedCountry}`);
      const subcatData = await subcatRes.json();
      if (subcatData.success) {
        setSubcategories(subcatData.subcategories);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to fetch admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-indigo-600" />
            Education & Tuition Admin
          </h1>
          <p className="text-gray-500">Manage educational services and configurations</p>
        </div>
        
        <Select value={selectedCountry} onValueChange={setSelectedCountry}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
            <SelectItem value="LK">🇱🇰 Sri Lanka</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.tutors?.total || 0}</p>
                <p className="text-sm text-gray-500">Total Tutors</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.tutors?.active || 0}</p>
                <p className="text-sm text-gray-500">Active Tutors</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.tutors?.pending || 0}</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.bookings || 0}</p>
                <p className="text-sm text-gray-500">Bookings</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="subcategories">
            <Settings className="w-4 h-4 mr-2" />
            Subcategories
          </TabsTrigger>
          <TabsTrigger value="exam-calendar">
            <Calendar className="w-4 h-4 mr-2" />
            Exam Calendar
          </TabsTrigger>
          <TabsTrigger value="tutors">
            <Users className="w-4 h-4 mr-2" />
            Tutors
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Subcategories Tab */}
        <TabsContent value="subcategories">
          <Card>
            <CardHeader>
              <CardTitle>Subcategory Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subcategory</TableHead>
                    <TableHead>Services</TableHead>
                    <TableHead>Card Type</TableHead>
                    <TableHead>Country Specific</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subcategories.map((subcat) => (
                    <TableRow key={subcat.slug}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{subcat.icon}</span>
                          <div>
                            <p className="font-medium">{subcat.name}</p>
                            <p className="text-xs text-gray-500">{subcat.slug}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{subcat.service_count}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{subcat.card_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {subcat.country_only ? (
                          <Badge className="bg-indigo-100 text-indigo-700">{selectedCountry} Only</Badge>
                        ) : (
                          <Badge variant="secondary">Global</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exam Calendar Tab */}
        <TabsContent value="exam-calendar">
          <ExamCalendarTab country={selectedCountry} />
        </TabsContent>

        {/* Tutors Tab */}
        <TabsContent value="tutors">
          <Card>
            <CardHeader>
              <CardTitle>Tutor Management</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-500 text-center py-8">
                Tutor management features coming soon...
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Country Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Verification Requirements</h4>
                  <p className="text-sm text-gray-500">
                    Configure minimum verification levels for different service types.
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Disclaimers</h4>
                  <p className="text-sm text-gray-500">
                    Manage disclaimer messages for education services.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
